import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    n: "01",
    title: "Rasenpflege",
    desc: "Mähen, Vertikutieren, Düngen und Nachsaat für einen dichten, sattgrünen Rasen über die ganze Saison.",
    img: "https://images.unsplash.com/photo-1558904541-efa843a96f01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxsYXduJTIwY2FyZSUyMGdyYXNzJTIwbGFuZHNjYXBpbmd8ZW58MHx8fHwxNzg0NjQ2MDE3fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-8",
  },
  {
    n: "02",
    title: "Heckenschnitt",
    desc: "Formschnitt und Pflege Ihrer Hecken — präzise, sauber und fachgerecht entsorgt.",
    img: "https://images.pexels.com/photos/32427439/pexels-photo-32427439.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-4",
  },
  {
    n: "03",
    title: "Baumpflege",
    desc: "Alles rund um den Baum.",
    img: "https://images.pexels.com/photos/35089307/pexels-photo-35089307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-4",
  },
  {
    n: "04",
    title: "Pflanzenpflege",
    desc: "Beetpflege, Bewässerung und Neupflanzungen, damit Ihre Pflanzen das ganze Jahr gedeihen.",
    img: "https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxwbGFudCUyMGNhcmUlMjBnYXJkZW5pbmclMjBoYW5kc3xlbnwwfHx8fDE3ODQ2NDYwMTd8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-8",
  },
  {
    n: "05",
    title: "Hausmeisterservice",
    desc: "Regelmäßige Pflege und kleine Instandhaltungen rund ums Haus — zuverlässig und diskret.",
    img: "https://images.unsplash.com/photo-1776269077163-e26abbc6189d?fm=jpg&q=75&w=1200&auto=format&fit=crop",
    span: "lg:col-span-6",
  },
  {
    n: "06",
    title: "Entrümpelung",
    desc: "Wir räumen Keller, Garage, Dachboden oder Garten leer — sauber, respektvoll und fachgerecht entsorgt.",
    img: "https://images.unsplash.com/photo-1709804945989-c8be542e04db?fm=jpg&q=75&w=1200&auto=format&fit=crop",
    span: "lg:col-span-6",
  },
];

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Services() {
  const scrollTo = () =>
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 sm:mb-20">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-4">
            Was wir tun
          </p>
          <h2 className="font-display font-bold tracking-tighter text-yx-primary text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[0.95]">
            Rundum­pflege für Ihren Garten.
          </h2>
        </div>
        <p className="max-w-sm text-yx-muted leading-relaxed">
          Unser Leistungsspektrum — und alles Weitere gerne auf Anfrage. Fehlt Ihnen
          etwas? Sagen Sie es uns einfach.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.n}
            custom={i}
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            data-testid={`service-card-${s.n}`}
            className={`group relative overflow-hidden rounded-sm bg-yx-surface ${s.span}`}
          >
            <div className="aspect-[16/10] lg:aspect-auto lg:h-[340px] overflow-hidden">
              <img
                src={s.img}
                alt={s.title}
                className="img-treat h-full w-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute inset-0 p-7 sm:p-8 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <span className="font-display text-sm font-semibold text-white/80">
                  {s.n}
                </span>
                <ArrowUpRight
                  size={22}
                  className="text-white/0 group-hover:text-white transition-all duration-300 -translate-x-1 group-hover:translate-x-0"
                />
              </div>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                  {s.title}
                </h3>
                <p className="max-w-md text-sm text-white/85 leading-relaxed opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
                  {s.desc}
                </p>
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-sm border border-yx-border bg-white p-6 sm:p-8">
        <p className="font-display text-xl sm:text-2xl font-medium text-yx-primary flex-1">
          Etwas anderes im Kopf? Wir setzen auch Ihren individuellen Wunsch um.
        </p>
        <button
          data-testid="services-cta"
          onClick={scrollTo}
          className="rounded-full bg-yx-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-yx-secondary whitespace-nowrap"
        >
          Wunsch anfragen
        </button>
      </div>
    </section>
  );
}
